import React from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { BUSHING_TYPES } from '@/constants/bushingTypes';
import type { BushingType } from '@/types/bushing';

interface SORComboBoxProps {
  /** Current SOR number / datasheet string. */
  sorValue: string | null;
  /** Available SOR items (dynamically populated from upload or BYD presets). */
  sorItems: string[];
  /** Callback when SOR selection changes. */
  onSorChange: (value: string | null) => void;
  /** Callback when the user types free text (syncs to store without SOR lookup). */
  onSorInputChange?: (value: string) => void;
  /** Whether the SOR combo is disabled. */
  sorDisabled?: boolean;

  /** Currently selected bushing type. */
  bushingType: BushingType;
  /** Callback when bushing type changes. */
  onBushingTypeChange: (type: BushingType) => void;
}

/**
 * SORComboBox — Two MUI Autocomplete fields side by side:
 *   1. SOR number / BYD datasheet (dynamic items)
 *   2. Bushing type (fixed list: A1, B1, C1, D1, BYD EV Voided, BYD EV Solid)
 */
const SORComboBox: React.FC<SORComboBoxProps> = ({
  sorValue,
  sorItems,
  onSorChange,
  onSorInputChange,
  sorDisabled = false,
  bushingType,
  onBushingTypeChange,
}) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    {/* SOR number / BYD datasheet */}
    <Autocomplete
      size="small"
      sx={{ flex: 1, minWidth: 0 }}
      options={sorItems}
      value={sorValue}
      onChange={(_e, v) => onSorChange(v)}
      onInputChange={(_e, v, reason) => {
        if (reason === 'input' && onSorInputChange) onSorInputChange(v);
      }}
      disabled={sorDisabled}
      renderInput={(params) => (
        <TextField {...params} label="Part number" placeholder="Select or type…" />
      )}
      freeSolo
      autoHighlight
    />

    {/* Bushing type (fixed list) */}
    <Autocomplete<BushingType, false, true, false>
      size="small"
      sx={{ flex: 1, minWidth: 0 }}
      options={BUSHING_TYPES}
      value={bushingType}
      onChange={(_e, v) => {
        if (v) onBushingTypeChange(v);
      }}
      disableClearable
      renderInput={(params) => (
        <TextField {...params} label="Bushing Type" />
      )}
      autoHighlight
    />
  </Box>
);

export default React.memo(SORComboBox);
